/**
 * Rules for letting a timesheet entry's description "follow" its activity.
 *
 * Each activity (product) carries its own standard description. When a user
 * picks or changes the activity on an entry, we pre-fill the description from
 * the activity — but only when doing so won't clobber text the user authored.
 * These two pure helpers encode that decision so both the draft and existing
 * day-list cards (and their tests) stay in lockstep.
 */

/** True when the current description still looks activity-derived rather than
 *  user-authored: it's blank, or it exactly matches the activity's own
 *  (`seed`) description. Trims both sides and treats null/blank as empty so a
 *  whitespace-only field counts as pristine. */
export function isDescriptionPristine(current: string, seed: string | null | undefined): boolean {
  const c = current.trim();
  return c === '' || c === (seed ?? '').trim();
}

/** The description to apply when the activity changes, or `undefined` to leave
 *  the current text untouched. Populates from the newly-picked activity's
 *  `nextSeed` only when it has a non-empty description (never auto-blanks — the
 *  server rejects clearing the description on a row that has hours) AND the
 *  current text is `pristine` (never authored by the user). */
export function descriptionForActivityChange(
  nextSeed: string | null | undefined,
  pristine: boolean,
): string | undefined {
  const next = (nextSeed ?? '').trim();
  if (next === '') return undefined;
  return pristine ? next : undefined;
}
